import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface ShowcaseItem {
  id: string;
  name: string;
  years: string;
  image: string;
  description: string;
  slug: string;
}

const placeholderItems: ShowcaseItem[] = [
  {
    id: "placeholder-1",
    name: "Margaret Johnson",
    years: "1942 - 2024",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&q=80",
    description: "A loving mother and grandmother",
    slug: "",
  },
  {
    id: "placeholder-2",
    name: "Robert Williams",
    years: "1938 - 2023",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
    description: "A devoted father and veteran",
    slug: "",
  },
  {
    id: "placeholder-3",
    name: "Eleanor Davis",
    years: "1950 - 2024",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800&q=80",
    description: "An inspiring teacher and mentor",
    slug: "",
  },
  {
    id: "placeholder-4",
    name: "James Thompson",
    years: "1945 - 2023",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=800&q=80",
    description: "A passionate musician and friend",
    slug: "",
  },
];

export const Showcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showcaseItems, setShowcaseItems] = useState<ShowcaseItem[]>(placeholderItems);
  const [rotationOffset, setRotationOffset] = useState(0);

  useEffect(() => {
    fetchFeaturedTributes();
  }, []);

  const fetchFeaturedTributes = async () => {
    try {
      // Fetch featured tributes that are public and not password protected
      const { data: tributes, error } = await supabase
        .from('tributes')
        .select(`
          id,
          person_name,
          birth_date,
          death_date,
          intro_text,
          slug
        `)
        .eq('is_featured', true)
        .eq('status', 'published')
        .eq('is_password_protected', false)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      if (tributes && tributes.length > 0) {
        // Fetch first photo for each tribute
        const tributeIds = tributes.map(t => t.id);
        const { data: photos } = await supabase
          .from('tribute_photos')
          .select('tribute_id, original_url, enhanced_url')
          .in('tribute_id', tributeIds)
          .eq('status', 'uploaded')
          .order('display_order', { ascending: true });

        // Create a map of tribute_id to first photo
        const photoMap: Record<string, string> = {};
        photos?.forEach(photo => {
          if (!photoMap[photo.tribute_id]) {
            photoMap[photo.tribute_id] = photo.enhanced_url || photo.original_url;
          }
        });

        // Format tributes for display
        const formattedTributes: ShowcaseItem[] = tributes.map((tribute, index) => {
          const birthYear = tribute.birth_date ? new Date(tribute.birth_date).getFullYear() : null;
          const deathYear = tribute.death_date ? new Date(tribute.death_date).getFullYear() : null;
          const years = birthYear && deathYear 
            ? `${birthYear} - ${deathYear}` 
            : birthYear 
              ? `Born ${birthYear}` 
              : deathYear 
                ? `- ${deathYear}` 
                : '';

          return {
            id: tribute.id,
            name: tribute.person_name,
            years,
            image: photoMap[tribute.id] || placeholderItems[index % placeholderItems.length].image,
            description: tribute.intro_text || 'A life remembered with love',
            slug: tribute.slug,
          };
        });

        // If more than 4, set up rotation; otherwise fill with placeholders
        if (formattedTributes.length >= 4) {
          // Randomize starting offset for rotation
          setRotationOffset(Math.floor(Math.random() * formattedTributes.length));
          setShowcaseItems(formattedTributes);
        } else {
          // Fill remaining slots with placeholders
          const filledItems = [...formattedTributes];
          let placeholderIndex = 0;
          while (filledItems.length < 4) {
            filledItems.push(placeholderItems[placeholderIndex % placeholderItems.length]);
            placeholderIndex++;
          }
          setShowcaseItems(filledItems);
        }
      }
    } catch (error) {
      console.error('Error fetching featured tributes:', error);
      // Keep placeholder items on error
    }
  };

  // Get the 4 items to display (with rotation for larger sets)
  const displayItems = showcaseItems.length > 4
    ? Array.from({ length: 4 }, (_, i) => showcaseItems[(rotationOffset + i) % showcaseItems.length])
    : showcaseItems.slice(0, 4);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % displayItems.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + displayItems.length) % displayItems.length);
  };

  const renderCard = (item: ShowcaseItem, index: number) => {
    const isPlaceholder = item.id.startsWith('placeholder');
    
    const cardContent = (
      <>
        <div className="aspect-[3/4] overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-16 left-0 right-0 p-4 text-white transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <p className="text-white font-bold text-sm">View this tribute</p>
        </div>
        <div className="p-4 bg-card">
          <h3 className="font-semibold text-foreground">{item.name}</h3>
          <p className="text-muted-foreground text-sm">{item.years}</p>
        </div>
      </>
    );

    const cardClasses = "group relative bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer block";

    if (isPlaceholder) {
      return (
        <div key={item.id + '-' + index} className={cardClasses}>
          {cardContent}
        </div>
      );
    }

    return (
      <Link key={item.id + '-' + index} to={`/see/${item.slug}`} className={cardClasses}>
        {cardContent}
      </Link>
    );
  };

  return (
    <section className="py-24 warm-gradient">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Every Life Has a Story Worth Telling
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how families are turning memories into beautiful, enduring tributes.
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {displayItems.map((item, index) => renderCard(item, index))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <div className="overflow-hidden rounded-2xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {displayItems.map((item, index) => {
                const isPlaceholder = item.id.startsWith('placeholder');
                return (
                  <div key={item.id + '-mobile-' + index} className="w-full flex-shrink-0 px-2">
                    {isPlaceholder ? (
                      <div className="bg-card rounded-2xl overflow-hidden shadow-md">
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground">{item.name}</h3>
                          <p className="text-muted-foreground text-sm">{item.years}</p>
                          <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{item.description}</p>
                        </div>
                      </div>
                    ) : (
                      <Link to={`/see/${item.slug}`} className="bg-card rounded-2xl overflow-hidden shadow-md block">
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground">{item.name}</h3>
                          <p className="text-muted-foreground text-sm">{item.years}</p>
                          <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{item.description}</p>
                        </div>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <Button
              variant="outline"
              size="icon"
              onClick={prevSlide}
              className="rounded-full"
              aria-label="Previous tribute"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex gap-2" role="tablist" aria-label="Tribute slides">
              {displayItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-accent" : "bg-border"
                  }`}
                  aria-label={`Go to slide ${index + 1}: ${item.name}`}
                  aria-selected={index === currentIndex}
                  role="tab"
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={nextSlide}
              className="rounded-full"
              aria-label="Next tribute"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
